import React, { useState, useCallback, Suspense, lazy, useMemo } from 'react';
import { Link } from 'react-router';
import { FaMapMarkerAlt, FaStar } from 'react-icons/fa';
import cn from 'classnames';
import 'mapbox-gl/dist/mapbox-gl.css';
import './Map.css';
import { MAPBOX_TOKEN, MAPBOX_STYLE_URL } from '../config/mapbox';
import { useMapResize } from '../hooks/useMapResize';

// Lazy loading of Mapbox components
const Map = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.Map })));
const Marker = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.Marker })));
const NavigationControl = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.NavigationControl })));
const FullscreenControl = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.FullscreenControl })));
const GeolocateControl = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.GeolocateControl })));

import { SitesMapFilter } from './SitesMapFilter';

interface SitesMapProps {
  projects: any[];
  showFilter?: boolean;
}

interface FilterState {
  searchTerm: string;
  selectedProjects: string[];
  showDefaultOnly: boolean;
}

const VIEWPORT = {
  latitude: 39.8283,  // Center of USA
  longitude: -98.5795,  // Center of USA
  zoom: 3.5,
  maxZoom: 18,
  minZoom: 2,
  bearing: 0,
  pitch: 0,
};

// Function to extract city from site
const getCityFromSite = (site: any): string => {
  if (!site) return '';
  
  if (site.google_address_data) {
    const { city, state, locality, administrative_area_level_1 } = site.google_address_data;
    
    if (locality && administrative_area_level_1) {
      return `${locality}, ${administrative_area_level_1}`;
    }
    if (locality) {
      return locality;
    }
    
    if (city && state) {
      return `${city}, ${state}`;
    }
    if (city) {
      return city;
    }
  }
  
  if (site.address) {
    const parts = site.address.split(',').map((part: string) => part.trim());
    if (parts.length >= 2) {
      return `${parts[1]}, ${parts[2] || parts[parts.length - 1]}`;
    }
    return parts[0];
  }
  
  return site.name || 'Unknown location';
};

// Function to get project color
const getProjectColor = (project: any): string => {
  const hasNoItems = (!project.items || project.items.length === 0) && 
                     (!project.actionItems || project.actionItems.length === 0);
  
  if (hasNoItems) {
    return 'bg-green-500';
  }
  
  const allItemsLowRisk = project.items?.every((item: any) => 
    !item.risks || item.risks === null || item.risks.toLowerCase() === 'low' || item.risks.toLowerCase() === 'none'
  ) ?? true;
  
  const allActionItemsLowRisk = project.actionItems?.every((item: any) => 
    !item.priority || item.priority === null || item.priority.toLowerCase() === 'low'
  ) ?? true;
  
  if (allItemsLowRisk && allActionItemsLowRisk) {
    return 'bg-green-500';
  }
  
  if (project.status === 'NOT_STARTED') return 'bg-[#666]';
  if (project.status === 'COMPLETED') return 'bg-orange-400';
  if (project.status === 'IN_PROGRESS') return 'bg-red-500';
  
  return 'bg-[#666]';
};

export const SitesMap: React.FC<SitesMapProps> = ({ projects, showFilter = true }) => {
  const watchMapResize = useMapResize();
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    selectedProjects: [],
    showDefaultOnly: false
  });

  // Extract all sites from all projects
  const allSites = useMemo(() => {
    const sites: any[] = [];
    
    projects.forEach(project => {
      if (project.sites && project.sites.length > 0) {
        project.sites.forEach((site: any) => {
          sites.push({
            ...site,
            projectId: project.id,
            projectName: project.name,
            projectStatus: project.status,
            projectColor: getProjectColor(project)
          });
        });
      }
    });
    
    return sites;
  }, [projects]);

  // Filter sites based on current filters
  const filteredSites = useMemo(() => {
    return allSites.filter(site => {
      // Project filter
      if (filters.selectedProjects.length > 0 && !filters.selectedProjects.includes(site.projectId)) {
        return false;
      }
      
      // Default sites only filter
      if (filters.showDefaultOnly && !site.is_default) {
        return false;
      }
      
      // Search filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const siteName = site.name?.toLowerCase() || '';
        const siteAddress = site.address?.toLowerCase() || '';
        const projectName = site.projectName?.toLowerCase() || '';
        
        if (!siteName.includes(searchLower) && 
            !siteAddress.includes(searchLower) && 
            !projectName.includes(searchLower)) {
          return false;
        }
      }
      
      return true;
    });
  }, [allSites, filters]);

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  return (
    <div className="relative w-full h-screen">
      <Suspense fallback={<div className="w-full h-full bg-[#f5f5f5] flex items-center justify-center">Loading map...</div>}>
        <Map
          initialViewState={VIEWPORT}
          style={{ width: '100%', height: '100%' }}
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle={MAPBOX_STYLE_URL}
          onLoad={(evt) => watchMapResize(evt.target)}
        >
          {/* Map Controls */}
          <NavigationControl position="top-right" />
          <FullscreenControl position="top-right" />
          <GeolocateControl position="top-right" />
          
          {/* Site Markers */}
          {filteredSites.map((site) => {
            if (!site.geo_location || !Array.isArray(site.geo_location) || site.geo_location.length < 2) {
              return null;
            }
            
            const [latitude, longitude] = site.geo_location;
            
            return (
              <Marker
                key={`${site.projectId}-${site.id}`}
                longitude={longitude}
                latitude={latitude}
                anchor="bottom"
              >
                <Link
                  to={`/projects/${site.projectId}`}
                  className="block"
                >
                  <div className="relative group">
                    {/* Marker */}
                    <div className={cn(
                      'w-8 h-8 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.08)] flex items-center justify-center text-white cursor-pointer transition-all duration-200 hover:scale-110',
                      site.projectColor
                    )}>
                      <FaMapMarkerAlt size={16} />
                    </div>
                    
                    {/* Default site indicator */}
                    {site.is_default && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                        <FaStar size={8} className="text-white" />
                      </div>
                    )}
                    
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      <div className="bg-white rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-[#eaeaea] p-3 min-w-64 max-w-80">
                        <div className="flex items-start space-x-2">
                          <div className={cn('w-3 h-3 rounded-full flex-shrink-0 mt-1', site.projectColor)}></div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-[#171717] truncate">{site.name}</div>
                            <div className="text-sm text-[#555] truncate">{getCityFromSite(site)}</div>
                            <div className="text-xs text-[#888] truncate">{site.projectName}</div>
                            {site.is_default && (
                              <div className="text-xs text-yellow-600 font-medium mt-1 flex items-center">
                                <FaStar size={8} className="mr-1" />
                                Default Site
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </Marker>
            );
          })}
        </Map>
      </Suspense>
      
      {/* Filter Component */}
      {showFilter && (
        <SitesMapFilter 
          projects={projects}
          onFilterChange={handleFilterChange}
        />
      )}
      
      {/* Stats */}
      <div className="absolute bottom-4 right-4 z-50 bg-white rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-[#eaeaea] p-3">
        <div className="text-sm text-[#555]">
          <div className="font-medium">Sites: {filteredSites.length}</div>
          <div className="text-xs text-[#888]">
            {filters.showDefaultOnly ? 'Default sites only' : 'All sites'}
          </div>
        </div>
      </div>
    </div>
  );
}; 